import { deviceType } from 'react-device-detect';
import config from '../common/config';

// add better typing to library variable
type DeviceType = 'mobile' | 'tablet' | 'browser'; // browser == desktop
const DEVICE = deviceType as DeviceType;

const MAX_IMG_WIDTH_MAP: Record<DeviceType, number> = {
	mobile: 640,
	tablet: 960,
	browser: 1920
};

const DEFAULT_MAX_IMG_WIDTH = MAX_IMG_WIDTH_MAP.browser;

type ImageOptions = {
	fit?: 'cover' | 'contain' | 'crop' | 'scale-down' | 'pad';
	width?: number;
	height?: number;
	dpr?: 1 | 2;
};

type VideoOptions = {
	fit?: 'contain' | 'cover' | 'scale-down';
	width?: number;
	height?: number;
	audio?: boolean;
	duration?: string;
	time?: string;
};

function resolveCdnHost(cdnHost: string): string {
	return cdnHost.endsWith('/') ? cdnHost : `${cdnHost}/`;
}

function buildCdnImageUri(assetPath: string, cdnHost: string, options?: ImageOptions): string {
	const base = resolveCdnHost(cdnHost);
	const optionsString = buildOptionsString(DEVICE, options);
	if (optionsString) {
		return new URL(`/cdn-cgi/image/${optionsString}`, base).toString() + assetPath;
	}
	return new URL(assetPath, base).toString();
}

/** Event cover images on staging live in the staging GCS bucket, not the prod CDN host. */
function getEventCdnHost(): string {
	if (config.deployment !== 'production') {
		return `https://${config.firebase.storageBucket}/`;
	}
	return config.cdnHost;
}

export function getCdnImageUri(
	assetPath: string,
	options?: ImageOptions
): string {
	return buildCdnImageUri(assetPath, config.cdnHost, options);
}

export function getEventCdnImageUri(
	assetPath: string,
	options?: ImageOptions
): string {
	return buildCdnImageUri(assetPath, getEventCdnHost(), options);
}

export function getCdnVideoUri(
	assetPath: string,
	options?: VideoOptions
): string {
	const optionsString = buildVideoOptionsString(DEVICE, options);
	if (optionsString) {
		return new URL(`/cdn-cgi/media/${optionsString}`, config.cdnHost).toString() + assetPath;
	}
	return new URL(assetPath, config.cdnHost).toString();
}

const buildOptionsString = (device: DeviceType, options?: ImageOptions) => {
	const defaultWidth = MAX_IMG_WIDTH_MAP[device] || DEFAULT_MAX_IMG_WIDTH;

	const optionStrings = [];

	const addOption = (name: string, value?: string | number) => {
		if (value) {
			optionStrings.push(`${name}=${value}`);
		}
	};

	addOption('fit', options?.fit);
	addOption('dpr', options?.dpr);
	addOption('height', options?.height);
	addOption('width', options?.width || defaultWidth);

	return optionStrings.join(',');
};

/**
 * Cloudflare Media Transformations rejects odd dimensions with a 400
 * (`MEDIA_TRANSFORMATION_ERROR 9401: width: attribute must be an even integer`), which leaves
 * the <video> with nothing to play. Round to the nearest even integer instead.
 */
const toEvenDimension = (value?: number) => (value ? Math.round(value / 2) * 2 : value);

const buildVideoOptionsString = (device: DeviceType, options?: VideoOptions) => {
	if (!options || Object.keys(options).length === 0) {
		return '';
	}

	const defaultWidth = MAX_IMG_WIDTH_MAP[device] || DEFAULT_MAX_IMG_WIDTH;
	const width = toEvenDimension(options?.width || defaultWidth);
	const height = toEvenDimension(options?.height);

	const optionStrings = ['mode=video']; // Must be first parameter

	const addOption = (name: string, value?: string | number | boolean) => {
		if (value !== undefined && value !== null) {
			optionStrings.push(`${name}=${value}`);
		}
	};

	// 'cover' fit requires both width and height
	if (options?.fit === 'cover' && width && height) {
		addOption('fit', options.fit);
	}
 else if (options?.fit && options.fit !== 'cover') {
		addOption('fit', options.fit);
	}

	addOption('height', height);
	addOption('width', width);
	addOption('audio', options?.audio);
	addOption('duration', options?.duration);
	addOption('time', options?.time);

	return optionStrings.join(',');
};
